"use client";

import { useCallback, useEffect, useRef, useState, memo, useMemo } from "react";
import { cn } from "@marketing-sites/utils/cn";

// Performance optimized constants
const FADE_STEP_SIZE = 0.15;
const FADE_INTERVAL_MS = 32; // 30fps for smoother fade
const DOT_TRANSITION_DURATION = "0.25s"; // Faster transitions
const DISTANT_DOT_FADE_FACTOR = 0.1;
const INTERSECTION_THRESHOLD = 0.1;
const SCROLL_FADE_DELAY = 150;
const MOUSE_MOVE_THRESHOLD_SQUARED = 64; // 8px threshold squared to avoid sqrt

type MousePosition = {
  x: number;
  y: number;
};

type DotData = {
  x: number;
  y: number;
  key: string;
};

type DotProps = {
  x: number;
  y: number;
  baseRadius: number;
  mousePos: MousePosition;
  maxDistance: number;
  maxScale: number;
  className?: string;
  fadeMultiplier: number;
};

export type DotsInteractiveProps = { className?: string } & {
  dotSpacing?: number;
  dotRadius?: number;
  maxScale?: number;
  effectRadius?: number;
};

// Simple RAF throttle
const throttleRAF = <A extends unknown[]>(fn: (...args: A) => void) => {
  let rafId: number | null = null;
  return (...args: A) => {
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        fn(...args);
        rafId = null;
      });
    }
  };
};

// Fast distance calculation
const getDistanceSquared = (x1: number, y1: number, x2: number, y2: number) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return dx * dx + dy * dy;
};

// Highly optimized Dot component
const Dot = memo(
  ({
    x,
    y,
    baseRadius,
    mousePos,
    maxDistance,
    maxScale,
    fadeMultiplier,
    className,
  }: DotProps) => {
    const distanceSquared = getDistanceSquared(mousePos.x, mousePos.y, x, y);
    const maxDistanceSquared = maxDistance * maxDistance;

    let radius: number;

    // Early return for distant dots - avoid sqrt calculation
    if (distanceSquared > maxDistanceSquared) {
      const fadeScale = 1 + (fadeMultiplier - 1) * DISTANT_DOT_FADE_FACTOR;
      radius = baseRadius * fadeScale;
    } else {
      // Calculate scale for dots within effect radius
      // Use fast inverse sqrt approximation or regular sqrt - browser optimizes this
      const distance = Math.sqrt(distanceSquared);
      const normalizedDistance = Math.min(distance / maxDistance, 1);
      const baseScale = 1 + (maxScale - 1) * (1 - normalizedDistance);
      const finalScale = 1 + (baseScale - 1) * fadeMultiplier;
      radius = baseRadius * finalScale;
    }

    return (
      <circle
        cx={x}
        cy={y}
        r={radius}
        className={cn(
          "fill-[color-mix(in_oklab,var(--color-content-primary,currentColor)_15%,_transparent)]",
          className,
        )}
        style={{ transition: `r ${DOT_TRANSITION_DURATION} ease-out` }}
      />
    );
  },
  (prevProps, nextProps) => {
    // Quick exit if mouse didn't move - most common case
    if (
      prevProps.mousePos.x === nextProps.mousePos.x &&
      prevProps.mousePos.y === nextProps.mousePos.y
    ) {
      return (
        prevProps.fadeMultiplier === nextProps.fadeMultiplier &&
        prevProps.maxScale === nextProps.maxScale &&
        prevProps.className === nextProps.className
      );
    }

    // Calculate squared distances (faster than sqrt)
    const prevDistanceSq = getDistanceSquared(
      prevProps.mousePos.x,
      prevProps.mousePos.y,
      prevProps.x,
      prevProps.y,
    );
    const nextDistanceSq = getDistanceSquared(
      nextProps.mousePos.x,
      nextProps.mousePos.y,
      nextProps.x,
      nextProps.y,
    );

    // Compare squared distance change - if difference is small relative to values,
    // the actual distance change is also small
    // Using: |a² - b²| ≈ 2*avg(a,b)*|a-b| for similar a,b
    const avgDistanceSq = (prevDistanceSq + nextDistanceSq) / 2;
    const distanceSqDiff = Math.abs(prevDistanceSq - nextDistanceSq);
    // Threshold: equivalent to ~3px change in most cases
    const thresholdSq = avgDistanceSq * 0.01 + 9; // Adaptive threshold

    const distanceChangedSignificantly = distanceSqDiff > thresholdSq;

    // Skip re-render if distance didn't change significantly
    if (!distanceChangedSignificantly) {
      return (
        Math.abs(prevProps.fadeMultiplier - nextProps.fadeMultiplier) < 0.05 &&
        prevProps.maxScale === nextProps.maxScale &&
        prevProps.className === nextProps.className
      );
    }

    // Props changed, need to re-render
    return false;
  },
);

Dot.displayName = "Dot";

// Optimized fade effect hook
const useFadeEffect = () => {
  const [isHovering, setIsHovering] = useState(false);
  const [fadeMultiplier, setFadeMultiplier] = useState(1);
  const fadeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearFadeInterval = useCallback(() => {
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
  }, []);

  const startFadeOut = useCallback(() => {
    setIsHovering(false);
    clearFadeInterval();

    let currentMultiplier = 1;
    fadeIntervalRef.current = setInterval(() => {
      currentMultiplier -= FADE_STEP_SIZE;

      if (currentMultiplier <= 0) {
        setFadeMultiplier(0);
        clearFadeInterval();
      } else {
        setFadeMultiplier(currentMultiplier);
      }
    }, FADE_INTERVAL_MS);
  }, [clearFadeInterval]);

  const stopFadeOut = useCallback(() => {
    setIsHovering(true);
    setFadeMultiplier(1);
    clearFadeInterval();
  }, [clearFadeInterval]);

  useEffect(() => {
    return clearFadeInterval;
  }, [clearFadeInterval]);

  return {
    isHovering,
    fadeMultiplier,
    startFadeOut,
    stopFadeOut,
  };
};

// Optimized mouse tracking
const useOptimizedMouseTracking = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  isHovering: boolean,
  startFadeOut: () => void,
  stopFadeOut: () => void,
) => {
  const [mousePos, setMousePos] = useState<MousePosition>({
    x: -1000,
    y: -1000,
  });
  const [isVisible, setIsVisible] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMousePosRef = useRef<MousePosition>({
    x: -1000,
    y: -1000,
  });

  // Optimized RAF throttle that batches updates and only updates on significant movement
  const throttledSetMousePos = useMemo(
    () =>
      throttleRAF((pos: MousePosition) => {
        // Only update if mouse moved significantly
        const distanceSquared = getDistanceSquared(
          lastMousePosRef.current.x,
          lastMousePosRef.current.y,
          pos.x,
          pos.y,
        );
        if (distanceSquared > MOUSE_MOVE_THRESHOLD_SQUARED) {
          lastMousePosRef.current = pos;
          setMousePos(pos);
        }
      }),
    [],
  );

  // Page visibility detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Intersection Observer
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: INTERSECTION_THRESHOLD },
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [containerRef]);

  // Event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isVisible || !isPageVisible) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;
      const relativeY = e.clientY - rect.top;

      throttledSetMousePos({ x: relativeX, y: relativeY });

      if (!isHovering) {
        stopFadeOut();
      }
    };

    const handleMouseEnter = () => {
      if (!isHovering) {
        stopFadeOut();
      }
    };

    const handleMouseLeave = () => {
      if (isHovering) {
        startFadeOut();
      }
    };

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        if (isHovering) {
          startFadeOut();
        }
      }, SCROLL_FADE_DELAY);
    };

    container.addEventListener("mousemove", handleMouseMove, { passive: true });
    container.addEventListener("mouseenter", handleMouseEnter, {
      passive: true,
    });
    container.addEventListener("mouseleave", handleMouseLeave, {
      passive: true,
    });
    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [
    isVisible,
    isPageVisible,
    isHovering,
    startFadeOut,
    stopFadeOut,
    throttledSetMousePos,
    containerRef,
  ]);

  return mousePos;
};

export const DotsInteractive = memo(
  ({
    className,
    dotSpacing = 20,
    dotRadius = 1,
    maxScale = 5,
    effectRadius = 100,
  }: DotsInteractiveProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    const { isHovering, fadeMultiplier, startFadeOut, stopFadeOut } =
      useFadeEffect();
    const mousePos = useOptimizedMouseTracking(
      containerRef,
      isHovering,
      startFadeOut,
      stopFadeOut,
    );

    // Container size tracking with debouncing
    useEffect(() => {
      let timeoutId: NodeJS.Timeout;

      const updateSize = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setContainerSize({ width: rect.width, height: rect.height });
          }
        }, 100);
      };

      const resizeObserver = new ResizeObserver(updateSize);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      updateSize();
      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
      };
    }, []);

    // Pre-calculate dot positions (stable reference)
    const dotPositions = useMemo(() => {
      if (containerSize.width === 0 || containerSize.height === 0) {
        return [];
      }

      const positions: DotData[] = [];
      const cols = Math.ceil(containerSize.width / dotSpacing) + 1;
      const rows = Math.ceil(containerSize.height / dotSpacing) + 1;

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * dotSpacing;
          const y = j * dotSpacing;
          positions.push({
            x,
            y,
            key: `${i}-${j}`,
          });
        }
      }
      return positions;
    }, [containerSize.width, containerSize.height, dotSpacing]);

    // Render all dots - let CSS transitions and browser optimization handle performance
    // Filtering was expensive and unnecessary since browser handles many elements efficiently
    const visibleDots = useMemo(
      () =>
        dotPositions.map((dot) => (
          <Dot
            key={dot.key}
            x={dot.x}
            y={dot.y}
            baseRadius={dotRadius}
            mousePos={mousePos}
            maxDistance={effectRadius}
            maxScale={maxScale}
            fadeMultiplier={fadeMultiplier}
            className={className}
          />
        )),
      [
        dotPositions,
        mousePos,
        effectRadius,
        dotRadius,
        maxScale,
        fadeMultiplier,
        className,
      ],
    );

    return (
      <div
        ref={containerRef}
        className={cn(
          "absolute inset-0 h-full w-full overflow-hidden",
          className,
        )}
      >
        <svg
          width="100%"
          height="100%"
          className="absolute inset-0"
          style={{ pointerEvents: "none" }}
        >
          {visibleDots}
        </svg>
      </div>
    );
  },
);

DotsInteractive.displayName = "DotsInteractive";
